import { complianceEntitiesApi, payeesApi } from "@api";
import { queriesComplianceEntities, queriesPayees } from "@query";
import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { ComplianceEntity, Payee } from "@wingspanhq/wingspan-api-sdk";
import { useWSQueries, WSQueryCache } from "@ws-react-query";
import { useMemo } from "react";

import { QUERY_PAYEE } from "../../../../query/payee/keys";
import { getContractorComplianceEntityListRequest } from "../../../Payees/hooks/useContractorTaxCompliance";
import {
  RequirementTableCallback,
  RequirementTableItemData,
  RequirementTableRowConfig
} from "../../types";
import {
  buildConfigFields,
  sharedRequirementsQueriesConfig
} from "../../utils";

import { usePayeeTaxVerificationConfig } from "./configs/payee/usePayeeTaxVerificationConfig";
import { usePayerTaxVerificationConfig } from "./configs/payer/usePayerTaxVerificationConfig";
import { TaxVerificationAdditionalData } from "./types";

const useContextQueries = (requirements: RequirementTableItemData[]) => {
  const taxVerificationRequirements = requirements.filter(
    requirement =>
      requirement.requirementType === RequirementType.TaxVerification &&
      requirement.payeeId
  );

  const payeeQueries = taxVerificationRequirements.map(requirement => ({
    queryKey: queriesPayees.getPayee.getQueryKey({
      payeeId: requirement.payeeId
    }),
    queryFn: () => payeesApi.getPayee({ payeeId: requirement.payeeId }),
    ...sharedRequirementsQueriesConfig
  }));

  // The payer-owned compliance entity per payee — its `usTaxStatus` gates the
  // W-9 "Download PDF" action (see usePayeeTaxVerificationConfig). Batched here,
  // mirroring the payee fetch, so the config reads it synchronously from cache
  // instead of calling a hook inside a per-row render callback.
  const complianceEntityQueries = taxVerificationRequirements.map(
    requirement => {
      const request = getContractorComplianceEntityListRequest(
        requirement.payeeId
      );
      return {
        queryKey:
          queriesComplianceEntities.listComplianceEntities.getQueryKey(request),
        queryFn: () => complianceEntitiesApi.listComplianceEntities(request),
        ...sharedRequirementsQueriesConfig
      };
    }
  );

  return useWSQueries({
    queries: [...payeeQueries, ...complianceEntityQueries]
  });
};

/** Reads the payer-owned compliance entity for a payee synchronously from cache. */
const getComplianceEntityFromCache = (
  payeeId: string
): ComplianceEntity | undefined =>
  queriesComplianceEntities.listComplianceEntities.getQueryData(
    getContractorComplianceEntityListRequest(payeeId)
  )?.data?.[0];

export const useTaxVerificationRequirementTableConfig = (
  callback: RequirementTableCallback,
  requirements: RequirementTableItemData[]
): RequirementTableRowConfig[RequirementType.TaxVerification] => {
  const payerTaxVerificationConfig = usePayerTaxVerificationConfig(callback);
  const payeeTaxVerificationConfig = usePayeeTaxVerificationConfig(callback);
  const contextQueries = useContextQueries(requirements);

  const queriesHash = contextQueries
    .map(query => query.dataUpdatedAt)
    .join(",");

  const fields = useMemo(
    () =>
      buildConfigFields<
        TaxVerificationAdditionalData,
        RequirementType.TaxVerification
      >({
        payerConfig: payerTaxVerificationConfig,
        payeeConfig: payeeTaxVerificationConfig,
        getAdditionalData: requirement => {
          const payee = queriesPayees.getPayee.getQueryData({
            payeeId: requirement.payeeId
          });
          return {
            payee,
            complianceEntity: getComplianceEntityFromCache(requirement.payeeId)
          };
        }
      })(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payerTaxVerificationConfig, payeeTaxVerificationConfig, queriesHash]
  );

  return {
    ...fields,
    getAdditionalData: requirement => {
      const payee = WSQueryCache.getQueryData<Payee>([
        QUERY_PAYEE,
        requirement.payeeId
      ]);
      return {
        payee,
        complianceEntity: getComplianceEntityFromCache(requirement.payeeId)
      };
    },
    isLoading: contextQueries.some(query => query.isLoading),
    dataUpdatedAt: queriesHash
  };
};
